/**
 * MoneyTimeline - plan_12, updated in plan_13 to plan_15 and plan_19 (Money plan).
 *
 * Two swipeable cards from the Diary money plan:
 *   - "Your money timeline": the big moments you're working toward, each with
 *     an emoji, a name, a month/year and an optional linked note.
 *   - "Every payday": what moves on its own each payday.
 *
 * Nothing is read from notes automatically - everything is filled in and
 * edited here. Saved on userData.money_timeline:
 *   { steps: [{ id, emoji, name, month (1-12 or null), year, noteId, fundId, done }],
 *     payday: { freq, next 'YYYY-MM-DD', transfers: [{ id, emoji, name, amount }] } }
 *
 * w2_11: steps no longer link to notes.
 *
 * `extraCards` lets Home add its own card (net worth) to the end of the swipe.
 *
 * w2_07: steps link to sinking funds or investment accounts.
 * plan_14: `funds` holds sinking funds and investment accounts
 *   ({ id, name, emoji, goal, balance, kind: 'fund' | 'investment' }; an
 *   investment's id is 'inv:<account id>'). A step's details only show once
 *   you tap it on the path, and that's where you tick off a step that isn't
 *   linked to a fund or account.
 */
import { useState, useRef } from "react";
import { createPortal } from "react-dom"; // w2_04
import PlanPath from "../common/PlanPath";
import { Sheet, ConfirmDialog, DateField } from "./plansShared";
import { formatCurrency, formatCurrencyWhole, formatCurrencyCompact } from "../../utils/formatters"; // w2_04

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STEP_EMOJIS = ['\u{1F6DF}', '\u{1F4B0}', '✈️', '\u{1F30D}', '\u{1F3E0}', '\u{1F37C}', '\u{1F393}', '\u{1F33B}', '\u{1F697}', '\u{1F48D}', '\u{1F389}', '⭐'];
const TRANSFER_EMOJIS = ['\u{1F6DF}', '\u{1F4C8}', '\u{1F33B}', '\u{1F437}', '\u{1F3E6}', '\u{1F4B3}', '\u{1F3E0}', '✈️', '\u{1F381}', '\u{1F4B0}'];
const FREQS = [['weekly', 'Weekly'], ['biweekly', 'Every 2 weeks'], ['semimonthly', 'Twice a month'], ['monthly', 'Monthly']];

const newId = () => 't' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const parseDay = (k) => { const [y, m, d] = String(k).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1, 12); };
const dayKey = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const lastDay = (y, m) => new Date(y, m + 1, 0).getDate();

// The next payday on or after today, rolled forward from the saved one.
export const nextPayday = (payday, today = new Date()) => {
  if (!payday || !payday.next) return null;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
  let d = parseDay(payday.next);
  let guard = 0;
  while (d < t && guard < 600) {
    guard += 1;
    if (payday.freq === 'weekly') d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7, 12);
    else if (payday.freq === 'monthly') {
      const day = parseDay(payday.next).getDate();
      const m = d.getMonth() + 1;
      const y = d.getFullYear() + (m > 11 ? 1 : 0);
      const mm = m % 12;
      d = new Date(y, mm, Math.min(day, lastDay(y, mm)), 12);
    } else if (payday.freq === 'semimonthly') {
      // The 15th and the last day of each month.
      if (d.getDate() < 15) d = new Date(d.getFullYear(), d.getMonth(), 15, 12);
      else if (d.getDate() < lastDay(d.getFullYear(), d.getMonth())) d = new Date(d.getFullYear(), d.getMonth(), lastDay(d.getFullYear(), d.getMonth()), 12);
      else d = new Date(d.getFullYear(), d.getMonth() + 1, 15, 12);
    } else d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 14, 12);
  }
  return d;
};

const stepWhen = (s) => (s.month ? `${MONTHS_SHORT[s.month - 1]} ${s.year}` : String(s.year || ''));
const stepSortKey = (s) => (Number(s.year) || 0) * 100 + (Number(s.month) || 0);
// plan_13: a step linked to a fund is done when that fund reaches its goal.
// Any other step is done when you mark it done. The date is only your
// estimate, so it never ticks a step off on its own.
const fundFor = (s, funds) => (s.fundId ? (funds || []).find((f) => f.id === s.fundId) || null : null);
const stepDone = (s, funds) => {
  const f = fundFor(s, funds);
  if (f) return f.goal > 0 && f.balance >= f.goal;
  return !!s.done;
};
const kindEmoji = (f) => (f && f.kind === 'investment' ? '\u{1F4C8}' : '\u{1F437}');
const fundLine = (f) => (f && f.goal > 0
  ? `${Math.min(100, Math.round((f.balance / f.goal) * 100))}% \u00b7 ${formatCurrencyWhole(f.balance)} of ${formatCurrencyWhole(f.goal)}`
  : null);

const C = {
  ink: '#2B2C31', soft: '#7A7A82', lab: '#9A8FBF', field: '#F7F5FB', line: '#EEEAF6',
  lilac: '#ECE6FA', lilacT: '#7B5FC9', mint: '#DFF3E7',
};
const cardStyle = (bg) => ({ background: bg || '#FFFFFF', borderRadius: 28, padding: '20px 18px 18px', flex: '0 0 100%', scrollSnapAlign: 'center', minWidth: 0 });
const badge = (bg, e) => (
  <span className="flex items-center justify-center flex-shrink-0" style={{ width: 50, height: 50, borderRadius: 999, background: bg, fontSize: 23 }}>{e}</span>
);
const boxStyle = (bg) => ({ display: 'flex', alignItems: 'center', gap: 12, background: bg || '#F4F1FB', borderRadius: 20, padding: '14px 16px', marginTop: 16 });
const blackBtn = { flexShrink: 0, background: '#1F1E24', color: '#FFFFFF', border: 0, borderRadius: 999, padding: '10px 15px', fontSize: 13.5, fontWeight: 600 };
const editLink = { display: 'block', margin: '12px auto 0', background: 'none', border: 0, padding: 0, fontSize: 13, fontWeight: 600, color: '#8B879A' };
const label = { display: 'block', fontSize: 14.5, fontWeight: 500, color: '#7A5DD3', margin: '18px 0 8px' };
const input = { width: '100%', background: '#F7F5FB', border: 0, borderRadius: 16, padding: '13px 15px', fontSize: 16, color: '#26252E', outline: 'none' };
const bigBtn = { display: 'block', width: '100%', background: '#1F1E24', color: '#FFFFFF', border: 0, borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 600, marginTop: 20 };
const dashedBtn = { display: 'block', width: '100%', background: 'transparent', border: '2px dashed #DCD3F4', borderRadius: 999, padding: 12, fontSize: 14.5, fontWeight: 600, color: '#7A5DD3', marginTop: 12 };

const EmojiPick = ({ value, onChange, options }) => {
  const [custom, setCustom] = useState('');
  return (
    <>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {options.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => onChange(e)}
            style={{ width: 42, height: 42, borderRadius: 14, border: 0, fontSize: 19, background: value === e ? C.lilac : C.field, boxShadow: value === e ? 'inset 0 0 0 2px #B9A6F0' : 'none' }}
          >
            {e}
          </button>
        ))}
      </div>
      <input
        type="text"
        value={custom}
        onChange={(ev) => { const v = ev.target.value.slice(0, 8); setCustom(v); if (v.trim()) onChange(v.trim()); }}
        placeholder="Or type any emoji"
        style={{ ...input, marginTop: 10 }}
      />
    </>
  );
};

const Row = ({ emoji, bg, title, sub, chip, onClick }) => (
  <button type="button" onClick={onClick} className="w-full flex items-center text-left" style={{ gap: 12, padding: '11px 0', background: 'none', border: 0, borderTop: `1px solid ${C.line}` }}>
    <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 14, background: bg || C.lilac, fontSize: 18 }}>{emoji}</span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#33343A' }}>{title}</span>
      <span style={{ display: 'block', fontSize: 12.5, color: '#8B879A' }}>{sub}</span>
      {[].concat(chip || []).map((c) => (
        <span key={c} style={{ display: 'inline-block', marginTop: 4, marginRight: 6, fontSize: 12, fontWeight: 600, color: C.lilacT, background: C.lilac, borderRadius: 999, padding: '3px 9px' }}>{c}</span>
      ))}
    </span>
    <span style={{ color: '#C4BADF', fontSize: 18 }}>{'›'}</span>
  </button>
);

// ------------------------------------------------------------- editors
const TimelineEditor = ({ timeline, notes, funds = [], onSave, onClose }) => {
  const [steps, setSteps] = useState(() => (timeline.steps || []).slice());
  const [edit, setEdit] = useState(null); // step being edited (copy) or null
  const [confirm, setConfirm] = useState(false);
  const sorted = steps.slice().sort((a, b) => stepSortKey(a) - stepSortKey(b));
  const commit = (next) => { setSteps(next); onSave({ ...timeline, steps: next }); };

  if (edit) {
    const isNew = !steps.some((s) => s.id === edit.id);
    const ok = edit.name.trim() && Number(edit.year) > 1900;
    return (
      <Sheet title={isNew ? 'Add a step' : 'Edit step'} onClose={() => setEdit(null)} track="#F5F2FA">
        <span style={{ ...label, marginTop: 0 }}>Emoji</span>
        <EmojiPick value={edit.emoji} onChange={(e) => setEdit({ ...edit, emoji: e })} options={STEP_EMOJIS} />
        <span style={label}>Name</span>
        <input type="text" value={edit.name} maxLength={20} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. Fund full" style={input} />
        <span style={label}>When</span>
        <div className="grid grid-cols-2" style={{ gap: 10 }}>
          <select value={edit.month || ''} onChange={(e) => setEdit({ ...edit, month: e.target.value ? Number(e.target.value) : null })} style={input}>
            <option value="">Any month</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" inputMode="numeric" value={edit.year} onChange={(e) => setEdit({ ...edit, year: e.target.value })} placeholder="Year" style={input} />
        </div>
        <span style={label}>Link to a fund or investment account (optional)</span>
        <select
          value={edit.fundId || ''}
          onChange={(e) => {
            const id = e.target.value || null;
            const f = id ? funds.find((x) => x.id === id) : null;
            const nm = f ? (f.kind === 'investment' ? `${f.name} goal` : `${f.name.replace(/ fund$/i, '')} full`).slice(0, 20) : '';
            setEdit({ ...edit, fundId: id, name: f && !edit.name.trim() ? nm : edit.name, emoji: f && f.emoji ? f.emoji : edit.emoji });
          }}
          style={input}
        >
          <option value="">Not linked</option>
          {funds.some((f) => f.kind !== 'investment') && (
            <optgroup label="Funds">
              {funds.filter((f) => f.kind !== 'investment').map((f) => <option key={f.id} value={f.id}>{(f.emoji ? f.emoji + ' ' : '') + f.name}</option>)}
            </optgroup>
          )}
          {funds.some((f) => f.kind === 'investment') && (
            <optgroup label="Investment accounts">
              {funds.filter((f) => f.kind === 'investment').map((f) => <option key={f.id} value={f.id}>{(f.emoji ? f.emoji + ' ' : '') + f.name}</option>)}
            </optgroup>
          )}
        </select>
        {(() => {
          const f = edit.fundId ? funds.find((x) => x.id === edit.fundId) : null;
          if (f) {
            return (
              <p style={{ margin: '8px 2px 0', fontSize: 13, color: '#8B879A', lineHeight: 1.45 }}>
                {f.goal > 0 ? `Ticks itself off when ${f.name} reaches its ${formatCurrencyWhole(f.goal)} goal. Now ${fundLine(f)}.` : `${f.name} doesn't have a goal yet. Set one on its page so this step can tick itself off.`}
              </p>
            );
          }
          return (
            <button
              type="button"
              role="switch"
              aria-checked={!!edit.done}
              onClick={() => setEdit({ ...edit, done: !edit.done })}
              className="w-full flex items-center justify-between"
              style={{ marginTop: 14, background: '#F7F5FB', border: 0, borderRadius: 16, padding: '13px 15px' }}
            >
              <span style={{ fontSize: 15, fontWeight: 500, color: '#26252E' }}>Mark as done</span>
              <span style={{ position: 'relative', width: 44, height: 26, borderRadius: 999, background: edit.done ? '#9ED9B6' : '#E4E0EC', flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: 3, left: edit.done ? 21 : 3, width: 20, height: 20, borderRadius: 999, background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.18)', transition: 'left .15s' }} />
              </span>
            </button>
          );
        })()}
        <button
          type="button"
          disabled={!ok}
          onClick={() => {
            const clean = { ...edit, name: edit.name.trim(), year: Number(edit.year) };
            commit(isNew ? [...steps, clean] : steps.map((s) => (s.id === clean.id ? clean : s)));
            setEdit(null);
          }}
          style={{ ...bigBtn, opacity: ok ? 1 : 0.4 }}
        >
          Save step
        </button>
        {!isNew && (
          <button type="button" onClick={() => setConfirm(true)} style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 0, fontSize: 14, fontWeight: 600, color: '#C04B7C' }}>Delete step</button>
        )}
        {confirm && (
          <ConfirmDialog
            title="Delete this step?"
            body={`${edit.name || 'This step'} will be taken off your timeline.`}
            confirmColor="#1F1E24"
            onCancel={() => setConfirm(false)}
            onConfirm={() => { setConfirm(false); commit(steps.filter((s) => s.id !== edit.id)); setEdit(null); }}
          />
        )}
      </Sheet>
    );
  }

  return (
    <Sheet title="Your money timeline" subtitle="The moments you're working toward!" /* plan_28 */ onClose={onClose} track="#F5F2FA">
      {sorted.length === 0 && <p style={{ margin: 0, fontSize: 14, color: '#8B879A' }}>No steps yet. Add the first one below!</p>}
      <div>
        {sorted.map((s) => (
          <Row
            key={s.id}
            emoji={s.emoji}
            title={s.name}
            sub={stepWhen(s) + (stepDone(s, funds) ? ' \u00b7 done \u2713' : fundFor(s, funds) && fundLine(fundFor(s, funds)) ? ' \u00b7 ' + fundLine(fundFor(s, funds)) : '')}
            chip={[fundFor(s, funds) ? kindEmoji(fundFor(s, funds)) + ' ' + fundFor(s, funds).name : null].filter(Boolean)}
            onClick={() => setEdit({ ...s })}
          />
        ))}
      </div>
      <button type="button" onClick={() => setEdit({ id: newId(), emoji: STEP_EMOJIS[0], name: '', month: null, year: new Date().getFullYear() + 1, noteId: null, fundId: null, done: false })} style={dashedBtn}>+ Add a step</button>
      <button type="button" onClick={onClose} style={bigBtn}>Done</button>
    </Sheet>
  );
};

const PaydayEditor = ({ timeline, onSave, onClose }) => {
  const start = timeline.payday || {};
  const [pay, setPay] = useState({ freq: start.freq || 'biweekly', next: start.next || dayKey(new Date()), transfers: (start.transfers || []).slice() });
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const commit = (next) => { setPay(next); onSave({ ...timeline, payday: next }); };

  if (edit) {
    const isNew = !pay.transfers.some((t) => t.id === edit.id);
    const ok = edit.name.trim() && Number(edit.amount) > 0;
    return (
      <Sheet title={isNew ? 'Add a transfer' : 'Edit transfer'} onClose={() => setEdit(null)} track="#F5F2FA">
        <span style={{ ...label, marginTop: 0 }}>Emoji</span>
        <EmojiPick value={edit.emoji} onChange={(e) => setEdit({ ...edit, emoji: e })} options={TRANSFER_EMOJIS} />
        <span style={label}>Where it goes</span>
        <input type="text" value={edit.name} maxLength={18} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. Emergency fund" style={input} />
        <span style={label}>Amount each payday</span>
        <input type="number" inputMode="decimal" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} placeholder="0.00" style={input} />
        <button
          type="button"
          disabled={!ok}
          onClick={() => {
            const clean = { ...edit, name: edit.name.trim(), amount: Number(edit.amount) };
            commit({ ...pay, transfers: isNew ? [...pay.transfers, clean] : pay.transfers.map((t) => (t.id === clean.id ? clean : t)) });
            setEdit(null);
          }}
          style={{ ...bigBtn, opacity: ok ? 1 : 0.4 }}
        >
          Save transfer
        </button>
        {!isNew && (
          <button type="button" onClick={() => setConfirm(true)} style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 0, fontSize: 14, fontWeight: 600, color: '#C04B7C' }}>Delete transfer</button>
        )}
        {confirm && (
          <ConfirmDialog
            title="Delete this transfer?"
            body={`${edit.name || 'This transfer'} will be taken off your payday.`}
            confirmColor="#1F1E24"
            onCancel={() => setConfirm(false)}
            onConfirm={() => { setConfirm(false); commit({ ...pay, transfers: pay.transfers.filter((t) => t.id !== edit.id) }); setEdit(null); }}
          />
        )}
      </Sheet>
    );
  }

  return (
    <Sheet title="Every payday" subtitle="When you get paid and what moves on its own." onClose={onClose} track="#F5F2FA">
      <span style={{ ...label, marginTop: 0 }}>How often</span>
      <div className="flex" style={{ background: '#ECE8F4', borderRadius: 999, padding: 3 }}>
        {FREQS.map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => commit({ ...pay, freq: k })}
            style={{ flex: 1, border: 0, borderRadius: 999, padding: '8px 2px', fontSize: 12.5, lineHeight: 1.2, fontWeight: pay.freq === k ? 600 : 500, color: pay.freq === k ? '#26252E' : '#6C6879', background: pay.freq === k ? '#FFFFFF' : 'transparent' }}
          >
            {l}
          </button>
        ))}
      </div>
      <span style={label}>{pay.freq === 'semimonthly' ? 'Next payday (the 15th or the last day)' : 'Next payday'}</span>
      <DateField value={pay.next} onChange={(v) => v && commit({ ...pay, next: v })} style={{ ...input, minHeight: 50, display: 'block' }} />
      <span style={label}>What moves on its own</span>
      <div>
        {pay.transfers.map((t) => (
          <Row key={t.id} emoji={t.emoji} bg="#DFF2E7" title={t.name} sub={formatCurrency(Number(t.amount) || 0)} onClick={() => setEdit({ ...t })} />
        ))}
      </div>
      <button type="button" onClick={() => setEdit({ id: newId(), emoji: TRANSFER_EMOJIS[0], name: '', amount: '' })} style={dashedBtn}>+ Add a transfer</button>
      <button type="button" onClick={onClose} style={bigBtn}>Done</button>
    </Sheet>
  );
};

// ------------------------------------------------------------------ cards
export const TimelineCard = ({ timeline, notes, funds = [], onSave, onEdit, onOpenNote, onExample, example = false, startOpen = null }) => { // w2_04: example
  const now = new Date();
  const [openId, setOpenId] = useState(startOpen); // plan_14: the step you tapped
  const steps = (timeline.steps || []).slice().sort((a, b) => stepSortKey(a) - stepSortKey(b));
  const upcoming = steps.filter((s) => !stepDone(s, funds));
  const tap = (id) => setOpenId(openId === id ? null : id);
  // plan_14: the path stays in date order. It starts at the step just before
  // your first unfinished one, and done steps are ticked wherever they sit.
  const firstOpen = steps.findIndex((s) => !stepDone(s, funds));
  const idx = firstOpen < 0 ? steps.length : firstOpen;
  const nowStep = { emoji: '✨', label: 'Now', sub: String(now.getFullYear()), done: true };
  const win = idx === 0 ? steps.slice(0, 4) : steps.slice(Math.max(0, Math.min(idx - 1, steps.length - 5)), Math.max(0, Math.min(idx - 1, steps.length - 5)) + 5);
  const nextId = firstOpen < 0 ? null : steps[firstOpen].id;
  const pathSteps = [
    ...(idx === 0 ? [nowStep] : []),
    ...win.map((s) => ({ emoji: s.emoji, label: s.name, sub: stepWhen(s), done: stepDone(s, funds), now: s.id === nextId, selected: openId === s.id, onClick: () => tap(s.id) })),
  ];
  let lastDoneAt = 0;
  pathSteps.forEach((st, i) => { if (st.done) lastDoneAt = i; });
  const shown = upcoming;
  const open = openId ? steps.find((s) => s.id === openId) || null : null;
  const openDone = open ? stepDone(open, funds) : false;
  const openFund = open ? fundFor(open, funds) : null;
  const pct = openFund && openFund.goal > 0 ? Math.min(100, (openFund.balance / openFund.goal) * 100) : 0;
  const setDone = (v) => onSave && onSave({ ...timeline, steps: (timeline.steps || []).map((s) => (s.id === open.id ? { ...s, done: v } : s)) });
  return (
    <div style={cardStyle()} data-testid="plan-timeline-card">
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: C.ink }}>Your money timeline {'✨'}</h3>
          {!example && <p style={{ margin: '5px 0 0', fontSize: 13.5, color: C.soft, lineHeight: 1.45 }}>
            {steps.length && !upcoming.length ? "You've reached every step!" : 'The moments you’re working toward!' /* w2_15 */}
          </p>}
        </div>
        {example ? <ExampleChip bg={C.lilac} ink="#7A5DD3" /> : badge(C.lilac, shown[0] ? shown[0].emoji : '\u{1F5FA}️')}
      </div>
      {steps.length > 0 && <PlanPath steps={pathSteps} progress={pathSteps.length > 1 ? lastDoneAt / (pathSteps.length - 1) : 0} />}
      {open ? (
        <div style={{ ...boxStyle(openDone ? '#EAF6EF' : undefined), alignItems: 'flex-start', flexDirection: 'column', gap: 0 }} data-testid="plan-step-details">
          <div className="flex items-center w-full" style={{ gap: 12 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 12.5, color: openDone ? '#4E9A6E' : C.lab, fontWeight: 500 }}>
                {openDone ? `Done ✓ · ${stepWhen(open)}` : `Aiming for ${stepWhen(open)}`}
              </span>
              <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 3, lineHeight: 1.3 }}>{open.emoji} {open.name}</span>
            </span>
            {!openFund && onSave && (openDone
              ? <button type="button" onClick={() => setDone(false)} style={{ ...blackBtn, background: '#FFFFFF', color: '#7A7A82' }}>Undo</button>
              : <button type="button" onClick={() => setDone(true)} style={blackBtn}>Done</button>)}
          </div>
          {openFund && (
            <div className="w-full" style={{ marginTop: 12 }}>
              {openFund.goal > 0 ? (
                <>
                  <span style={{ display: 'block', height: 7, borderRadius: 9, background: 'rgba(165,139,230,0.18)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${pct}%`, borderRadius: 9, background: openDone ? '#9ED9B6' : '#A58BE6' }} />
                  </span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#7A7A82', marginTop: 7, lineHeight: 1.4 }}>
                    {kindEmoji(openFund)} {openFund.name} {'·'} {fundLine(openFund)}
                  </span>
                  {!openDone && (
                    <span style={{ display: 'block', fontSize: 12, color: '#9A97A6', marginTop: 2 }}>Ticks itself off when it reaches its goal.</span>
                  )}
                </>
              ) : (
                <span style={{ display: 'block', fontSize: 12.5, color: '#7A7A82', lineHeight: 1.4 }}>
                  {kindEmoji(openFund)} {openFund.name} doesn't have a goal yet. Set one on its page so this ticks itself off.
                </span>
              )}
            </div>
          )}
        </div>
      ) : steps.length === 0 ? (
        <div style={boxStyle()}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1.3 }}>Add your first step, like a fund being full</span>
          </span>
          <button type="button" onClick={onEdit} style={blackBtn}>Add</button>
        </div>
      ) : null}
      {example ? null : steps.length === 0 && onExample ? (
        <button type="button" onClick={onExample} style={{ ...editLink, color: '#7A5DD3' }}>See an example {'›'}</button>
      ) : (
        <button type="button" onClick={onEdit} style={editLink}>{'✎'} Edit timeline</button>
      )}
    </div>
  );
};

export const PaydayCard = ({ timeline, onEdit, onExample, example = false, startOpen = null }) => { // w2_04: example
  const pay = timeline.payday || {};
  const transfers = pay.transfers || [];
  const nd = nextPayday(pay);
  const total = transfers.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const weekday = nd ? nd.toLocaleDateString('en-US', { weekday: 'long' }) : '';
  // plan_19: like the timeline, details only show once you tap a step.
  const [openId, setOpenId] = useState(startOpen);
  const tap = (id) => setOpenId(openId === id ? null : id);
  const steps = [
    { emoji: '\u{1F4BC}', label: 'Payday', sub: pay.freq === 'semimonthly' || pay.freq === 'monthly' ? (nd ? nd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '') : weekday, now: true, bg: '#FFFFFF', selected: openId === 'pay', onClick: () => tap('pay') },
    ...transfers.slice(0, 3).map((t) => ({ emoji: t.emoji, label: t.name, amount: formatCurrencyCompact(Number(t.amount) || 0), bg: 'rgba(255,255,255,0.75)', amountBg: 'rgba(255,255,255,0.8)', amountInk: '#2F8756', selected: openId === t.id, onClick: () => tap(t.id) })),
  ];
  const openT = openId && openId !== 'pay' ? transfers.find((t) => t.id === openId) : null;
  const ready = nd && transfers.length > 0;
  const perMonth = (amt) => {
    const a = Number(amt) || 0;
    if (pay.freq === 'weekly') return (a * 52) / 12;
    if (pay.freq === 'semimonthly') return a * 2;
    if (pay.freq === 'monthly') return a;
    return (a * 26) / 12;
  };
  return (
    <div style={cardStyle(C.mint)} data-testid="plan-payday-card">
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: C.ink }}>Every payday {'\u{1F4B8}'}</h3>
          {!example && <p style={{ margin: '5px 0 0', fontSize: 13.5, color: C.soft, lineHeight: 1.45 }}>Where your money goes on its own.</p>}
        </div>
        {example ? <ExampleChip bg="rgba(255,255,255,0.8)" ink="#2E8A55" /> : badge('rgba(255,255,255,0.7)', '\u{1F5D3}️')}
      </div>
      {transfers.length > 0 && <PlanPath flow steps={steps} />}
      {!ready ? (
        <div style={boxStyle('rgba(255,255,255,0.75)')}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1.3 }}>Add your payday and what moves on its own</span>
          </span>
          <button type="button" onClick={onEdit} style={blackBtn}>Add</button>
        </div>
      ) : openId === 'pay' ? (
        <div style={boxStyle('rgba(255,255,255,0.75)')} data-testid="plan-payday-details">
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, color: '#5E8F73', fontWeight: 500 }}>Next payday</span>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 3, lineHeight: 1.3 }}>
              {nd.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} {'·'} {formatCurrencyWhole(total)} moves on its own
            </span>
          </span>
        </div>
      ) : openT ? (
        <div style={boxStyle('rgba(255,255,255,0.75)')} data-testid="plan-payday-details">
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, color: '#5E8F73', fontWeight: 500 }}>Every payday</span>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 3, lineHeight: 1.3 }}>
              {openT.emoji} {formatCurrencyWhole(Number(openT.amount) || 0)} to {openT.name}
            </span>
            <span style={{ display: 'block', fontSize: 12.5, color: '#7A7A82', marginTop: 3 }}>About {formatCurrencyWhole(perMonth(openT.amount))} a month</span>
          </span>
        </div>
      ) : null}
      {example ? null : transfers.length === 0 && onExample ? (
        <button type="button" onClick={onExample} style={{ ...editLink, color: '#2E8A55' }}>See an example {'›'}</button>
      ) : (
        <button type="button" onClick={onEdit} style={editLink}>{'✎'} Edit payday</button>
      )}
    </div>
  );
};

// ------------------------------------------------------------- w2_04 examples
// "See an example" on an empty card: the card filled in with made-up steps.
const exampleTimeline = () => {
  const y = new Date().getFullYear();
  return {
    steps: [
      { id: 'ex1', emoji: '\u{1F6DF}', name: 'Fund full', month: 6, year: y + 1, done: false },
      { id: 'ex2', emoji: '✈️', name: 'Japan trip', month: 4, year: y + 2, done: false },
      { id: 'ex3', emoji: '\u{1F697}', name: 'Car paid', month: 12, year: y + 2, done: false },
      { id: 'ex4', emoji: '\u{1F3E1}', name: 'Deposit', month: null, year: y + 5, done: false },
    ],
  };
};
const examplePayday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7)); // the next Friday
  return {
    payday: {
      freq: 'biweekly',
      next: dayKey(d),
      transfers: [
        { id: 'ex1', emoji: '\u{1F6DF}', name: 'Savings', amount: 300 },
        { id: 'ex2', emoji: '\u{1F4C8}', name: 'Investing', amount: 200 },
        { id: 'ex3', emoji: '✈️', name: 'Travel', amount: 75 },
      ],
    },
  };
};

const ExampleChip = ({ bg, ink }) => (
  <span style={{ flexShrink: 0, background: bg, color: ink, borderRadius: 999, padding: '5px 11px', fontSize: 12, fontWeight: 600 }}>Example</span>
);

const ExampleSheet = ({ title, text, button, onButton, onClose, children }) => createPortal(
  <div style={{ position: 'fixed', inset: 0, zIndex: 5000 }} data-patch="w2_04">
    <div onClick={onClose} style={{ position: 'absolute', inset: '-60px 0', background: 'rgba(35,30,45,0.32)' }} />
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88vh', overflowY: 'auto', overscrollBehavior: 'contain', background: '#F3F0FA', borderRadius: '28px 28px 0 0', padding: '10px 16px calc(env(safe-area-inset-bottom, 0px) + 22px)' }}>
      <div style={{ width: 40, height: 5, borderRadius: 9, background: '#DCD5EC', margin: '0 auto 14px' }} />
      <div className="flex items-start justify-between" style={{ gap: 12, padding: '0 4px' }}>
        <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.3px', color: C.ink }}>{title}</h3>
        <button type="button" onClick={onClose} style={{ flexShrink: 0, border: 0, background: 'none', padding: 0, fontSize: 14, fontWeight: 600, color: '#8B879A' }}>Close</button>
      </div>
      <p style={{ margin: '6px 4px 0', fontSize: 14, color: C.soft, lineHeight: 1.5 }}>{text}</p>
      <div className="flex" style={{ marginTop: 14 }}>{children}</div>
      <button type="button" onClick={onButton} style={bigBtn}>{button}</button>
    </div>
  </div>,
  document.body
);

// ------------------------------------------------------------------ swipe
const MoneyTimeline = ({ timeline, notes = [], funds = [], onSave, onOpenNote, extraCards = [], style }) => {
  const tl = timeline || {};
  const [editor, setEditor] = useState(null); // 'timeline' | 'payday'
  const [page, setPage] = useState(0);
  const ref = useRef(null);
  const cards = [
    <TimelineCard key="t" timeline={tl} notes={notes} funds={funds} onSave={onSave} onEdit={() => setEditor('timeline')} onOpenNote={onOpenNote} onExample={() => setEditor('timeline-example')} />,
    <PaydayCard key="p" timeline={tl} onEdit={() => setEditor('payday')} onExample={() => setEditor('payday-example')} />,
    ...extraCards,
  ];
  const onScroll = () => {
    const el = ref.current;
    if (!el || !el.clientWidth) return;
    const i = Math.round(el.scrollLeft / (el.clientWidth + 12));
    if (i !== page) setPage(i);
  };
  return (
    <div style={style} data-testid="plan-money-timeline">
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex"
        style={{ gap: 12, overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', alignItems: 'flex-start' }}
      >
        {cards}
      </div>
      <div className="flex justify-center" style={{ gap: 5, marginTop: 12 }}>
        {cards.map((c, i) => (
          <span key={i} style={{ width: i === page ? 20 : 7, height: 7, borderRadius: 9, background: i === page ? '#A58BE6' : '#D9D2EE', transition: 'width .2s' }} />
        ))}
      </div>
      {editor === 'timeline' && <TimelineEditor timeline={tl} notes={notes} funds={funds} onSave={onSave} onClose={() => setEditor(null)} />}
      {editor === 'payday' && <PaydayEditor timeline={tl} onSave={onSave} onClose={() => setEditor(null)} />}
      {editor === 'timeline-example' && (
        <ExampleSheet
          title="Your money timeline"
          text="Add the moments you're saving for, with roughly when you'd like to reach them. Link a step to a fund and it ticks itself off when the fund is full! Not for you? You can hide it any time in Settings."
          button="Add my first step"
          onButton={() => setEditor('timeline')}
          onClose={() => setEditor(null)}
        >
          <TimelineCard example startOpen="ex1" timeline={exampleTimeline()} notes={[]} funds={[]} onEdit={() => {}} />
        </ExampleSheet>
      )}
      {editor === 'payday-example' && (
        <ExampleSheet
          title="Every payday"
          text="Add how often you're paid and your next payday. Then add the transfers that happen on their own each time, like money to savings, investments or a sinking fund!"
          button="Add my payday"
          onButton={() => setEditor('payday')}
          onClose={() => setEditor(null)}
        >
          <PaydayCard example startOpen="pay" timeline={examplePayday()} onEdit={() => {}} />
        </ExampleSheet>
      )}
    </div>
  );
};

export default MoneyTimeline;
